import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { profileService } from '../../services/profileService';
import Button from '../ui/Button';
import { dateUtils } from '../../utils/helpers';

const PasswordReset = ({ profileData, isOwnProfile }) => {
  const { isMobile, isTablet, getResponsiveValue } = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Responsive configurations
  const containerPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const sectionSpacing = getResponsiveValue({
    mobile: 'space-y-4',
    desktop: 'space-y-8'
  });

  const gridCols = getResponsiveValue({
    mobile: 'grid-cols-1',
    desktop: 'grid-cols-2'
  });

  const gridGap = getResponsiveValue({
    mobile: 'gap-4',
    desktop: 'gap-6'
  });

  const titleSize = getResponsiveValue({
    mobile: 'text-base',
    desktop: 'text-lg'
  });

  const subtitleSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const sectionTitleSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const cardTitleSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-base'
  });

  const labelSize = getResponsiveValue({
    mobile: 'text-xs',
    desktop: 'text-xs'
  });

  const textSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const cardPadding = getResponsiveValue({
    mobile: 'p-4',
    desktop: 'p-6'
  });

  const iconSize = getResponsiveValue({
    mobile: 'w-10 h-10',
    desktop: 'w-12 h-12'
  });

  const buttonSpacing = getResponsiveValue({
    mobile: 'flex-col space-y-2',
    desktop: 'flex-row space-x-2'
  });

  const buttonSize = getResponsiveValue({
    mobile: 'w-full',
    desktop: 'w-auto'
  });

  const handlePasswordReset = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      await profileService.sendPasswordResetEmail(profileData.email);
      
      setSuccess(true);
      setShowConfirm(false);
      
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOwnProfile) {
    return (
      <div className={containerPadding}>
        <div className="text-center py-12">
          <div className={`${iconSize} bg-error-subtle rounded-full flex items-center justify-center mx-auto mb-4`}>
            <svg className={`${getResponsiveValue({ mobile: 'w-6 h-6', desktop: 'w-8 h-8' })} text-error`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" 
              />
            </svg>
          </div>
          <h3 className={`${titleSize} font-semibold text-text-primary mb-2`}>Access Restricted</h3>
          <p className={`${subtitleSize} text-text-tertiary`}>You can only manage security settings for your own account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerPadding}>
      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-success-subtle border border-border-primary rounded-xl p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className={`${textSize} font-medium text-success`}>Password reset email sent!</p>
              <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-success mt-1`}>
                Check your email for further instructions. The link will expire in 1 hour.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-error-subtle border border-border-error rounded-xl p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className={`${textSize} text-error`}>{error}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h3 className={`${titleSize} font-semibold text-text-primary`}>Security Settings</h3>
        <p className={`${subtitleSize} text-text-tertiary`}>
          Manage your StateSync account security and password settings
        </p>
      </div>

      <div className={sectionSpacing}>
        {/* Account Security Overview */}
        <div>
          <h4 className={`${sectionTitleSize} font-semibold text-text-primary uppercase tracking-wide mb-4`}>
            Account Security
          </h4>
          
          <div className={`bg-surface-secondary rounded-xl ${cardPadding}`}>
            <div className={`grid ${gridCols} ${gridGap}`}>
              <div>
                <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                  Account Email
                </label>
                <p className={`${textSize} text-text-primary mt-1`}>{profileData.email}</p>
              </div>
              
              <div>
                <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                  Last Login
                </label>
                <p className={`${textSize} text-text-primary mt-1`}>
                  {profileData.lastLoginAt ? 
                    dateUtils.formatDate(profileData.lastLoginAt) : 
                    <span className="text-text-disabled italic">Never logged in</span>
                  }
                </p>
              </div>
              
              <div>
                <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                  Account Status
                </label>
                <p className={`${textSize} text-text-primary mt-1 capitalize`}>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    profileData.status === 'active' ? 'bg-success-subtle text-success' :
                    profileData.status === 'pending' ? 'bg-warning-subtle text-warning' :
                    'bg-surface-secondary text-text-secondary'
                  }`}>
                    {profileData.status}
                  </span>
                </p>
              </div>
              
              <div>
                <label className={`${labelSize} font-medium text-text-tertiary uppercase tracking-wide`}>
                  Security Score
                </label>
                <div className="flex items-center space-x-2 mt-1">
                  <div className="flex-1 bg-surface-primary rounded-full h-2">
                    <div className="bg-success h-2 rounded-full w-4/5"></div>
                  </div>
                  <span className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} font-medium text-text-primary`}>Good</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Password Management */}
        <div>
          <h4 className={`${sectionTitleSize} font-semibold text-text-primary uppercase tracking-wide mb-4`}>
            Password Management
          </h4>
          
          <div className={`bg-surface-elevated border border-border-primary rounded-xl ${cardPadding}`}>
            <div className={`flex ${isMobile ? 'flex-col space-y-4' : 'items-start space-x-4'}`}>
              <div className={`${iconSize} bg-info-subtle rounded-xl flex items-center justify-center flex-shrink-0`}>
                <svg className={`${getResponsiveValue({ mobile: 'w-5 h-5', desktop: 'w-6 h-6' })} text-info`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" 
                  />
                </svg>
              </div>
              
              <div className="flex-1">
                <h5 className={`${cardTitleSize} font-semibold text-text-primary mb-2`}>Reset Password</h5>
                <p className={`${textSize} text-text-tertiary mb-4`}>
                  Send a password reset link to your email address. You'll be able to create a new password using the secure link.
                </p>
                
                <div className="space-y-3">
                  {!showConfirm ? (
                    <Button 
                      onClick={() => setShowConfirm(true)}
                      variant="outline"
                      size="sm"
                      className={getResponsiveValue({ mobile: 'w-full', desktop: 'w-auto' })}
                    >
                      Reset Password
                    </Button>
                  ) : (
                    <div className="bg-warning-subtle border border-border-primary rounded-xl p-4">
                      <div className={`flex ${isMobile ? 'flex-col space-y-3' : 'items-start space-x-3'}`}>
                        <svg className="w-5 h-5 text-warning mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <h6 className={`${textSize} font-medium text-warning mb-1`}>Confirm Password Reset</h6>
                          <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-warning mb-3`}>
                            This will send a password reset email to <strong>{profileData.email}</strong>. 
                            The reset link will expire in 1 hour for security.
                          </p>
                          <div className={`flex ${buttonSpacing}`}>
                            <Button
                              onClick={handlePasswordReset}
                              loading={loading}
                              disabled={loading}
                              size="sm"
                              className={buttonSize}
                            >
                              {loading ? 'Sending...' : 'Send Reset Email'}
                            </Button>
                            <Button
                              onClick={() => setShowConfirm(false)}
                              variant="outline"
                              size="sm"
                              disabled={loading}
                              className={buttonSize}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Tips */}
        <div>
          <h4 className={`${sectionTitleSize} font-semibold text-text-primary uppercase tracking-wide mb-4`}>
            Security Guidelines
          </h4>
          
          <div className={`bg-surface-secondary rounded-xl ${cardPadding}`}>
            <h5 className={`${textSize} font-medium text-text-primary mb-2`}>Best Practices:</h5>
            <div className={`grid ${gridCols} ${gridGap}`}>
              <div className="space-y-4">
                <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-start space-x-3'}`}>
                  <div className="w-6 h-6 bg-success-subtle rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-success" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h6 className={`${textSize} font-medium text-text-primary`}>Strong Passwords</h6>
                    <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-tertiary`}>Use at least 8 characters with a mix of letters, numbers, and symbols.</p>
                  </div>
                </div>
                
                <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-start space-x-3'}`}>
                  <div className="w-6 h-6 bg-success-subtle rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-success" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h6 className={`${textSize} font-medium text-text-primary`}>Regular Updates</h6>
                    <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-tertiary`}>Change your password regularly, especially if you suspect it's been compromised.</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-start space-x-3'}`}>
                  <div className="w-6 h-6 bg-success-subtle rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-success" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h6 className={`${textSize} font-medium text-text-primary`}>Secure Browsing</h6>
                    <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-tertiary`}>Always log out from shared computers and avoid public Wi-Fi for government work.</p>
                  </div>
                </div>
                
                <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-start space-x-3'}`}>
                  <div className="w-6 h-6 bg-success-subtle rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-success" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h6 className={`${textSize} font-medium text-text-primary`}>Stay Alert</h6>
                    <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-tertiary`}>Be cautious of phishing emails and never share your StateSync credentials.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Activity */}
        <div>
          <h4 className={`${sectionTitleSize} font-semibold text-text-primary uppercase tracking-wide mb-4`}>
            Recent Activity
          </h4>
          
          <div className={`bg-surface-elevated border border-border-primary rounded-xl ${cardPadding}`}>
            <div className="space-y-4">
              <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-center space-x-3'} py-2`}>
                <div className="w-8 h-8 bg-success-subtle rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className={`flex ${isMobile ? 'flex-col space-y-1' : 'items-center justify-between'}`}>
                    <div>
                      <p className={`${textSize} font-medium text-text-primary`}>Account Login</p>
                      <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-tertiary`}>
                        {profileData.lastLoginAt ? 
                          dateUtils.formatDate(profileData.lastLoginAt) : 
                          'No previous logins recorded'
                        }
                      </p>
                    </div>
                    <span className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-success font-medium`}>Successful</span>
                  </div>
                </div>
              </div>
              
              <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-center space-x-3'} py-2`}>
                <div className="w-8 h-8 bg-info-subtle rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className={`flex ${isMobile ? 'flex-col space-y-1' : 'items-center justify-between'}`}>
                    <div>
                      <p className={`${textSize} font-medium text-text-primary`}>Account Created</p>
                      <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-tertiary`}>
                        {profileData.createdAt ? 
                          dateUtils.formatDate(profileData.createdAt) : 
                          'Account creation date not available'
                        }
                      </p>
                    </div>
                    <span className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-info font-medium`}>Completed</span>
                  </div>
                </div>
              </div>
              
              {profileData.profileUpdatedAt && (
                <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'items-center space-x-3'} py-2`}>
                  <div className="w-8 h-8 bg-accent-subtle rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className={`flex ${isMobile ? 'flex-col space-y-1' : 'items-center justify-between'}`}>
                      <div>
                        <p className={`${textSize} font-medium text-text-primary`}>Profile Updated</p>
                        <p className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-text-tertiary`}>
                          {dateUtils.formatDate(profileData.profileUpdatedAt)}
                        </p>
                      </div>
                      <span className={`${getResponsiveValue({ mobile: 'text-xs', desktop: 'text-xs' })} text-accent font-medium`}>Recent</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;